import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { InternalCandidatesTable } from "./internal-candidates/InternalCandidatesTable";
import { AddCandidateDialog } from "./internal-candidates/AddCandidateDialog";
import { ImportCandidatesDialog } from "./internal-candidates/ImportCandidatesDialog";
import { EditCandidateDialog } from "./internal-candidates/EditCandidateDialog";
import { DeleteCandidateDialog } from "./internal-candidates/DeleteCandidateDialog";

export interface InternalCandidate {
  id: string;
  sn: number;
  role: string;
  country: string;
  city: string;
  candidate_name: string;
  skillset: string;
  years_experience: number;
  linkedin_url: string;
  past_projects: string;
  user_id: string;
  project_id?: string;
  created_at: string;
  updated_at: string;
}

export function InternalCandidates() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  
  const [candidates, setCandidates] = useState<InternalCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<InternalCandidate | null>(null);

  useEffect(() => {
    fetchCandidates();
  }, [projectId]);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('internal_candidates')
        .select('*')
        .eq('project_id', projectId)
        .order('sn', { ascending: true });

      if (error) throw error;
      setCandidates(data || []);
    } catch (error) {
      console.error('Error fetching internal candidates:', error);
      toast.error("Failed to load internal candidates");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCandidate = async (candidateData: Omit<InternalCandidate, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to add candidates");
        return;
      }

      const { error } = await supabase
        .from('internal_candidates')
        .insert({
          ...candidateData,
          project_id: projectId,
          user_id: user.id
        });

      if (error) throw error;

      toast.success("Candidate added successfully");
      fetchCandidates();
      setAddDialogOpen(false);
    } catch (error) {
      console.error('Error adding candidate:', error);
      toast.error("Failed to add candidate");
    }
  };

  const handleEditCandidate = async (candidateData: Omit<InternalCandidate, 'user_id' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await supabase
        .from('internal_candidates')
        .update(candidateData)
        .eq('id', candidateData.id);

      if (error) throw error;

      toast.success("Candidate updated successfully");
      fetchCandidates();
      setEditDialogOpen(false);
      setSelectedCandidate(null);
    } catch (error) {
      console.error('Error updating candidate:', error);
      toast.error("Failed to update candidate");
    }
  };

  const handleDeleteCandidate = async (candidateId: string) => {
    try {
      const { error } = await supabase
        .from('internal_candidates')
        .delete()
        .eq('id', candidateId);

      if (error) throw error;

      toast.success("Candidate deleted successfully");
      fetchCandidates();
      setDeleteDialogOpen(false);
      setSelectedCandidate(null);
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.error("Failed to delete candidate");
    }
  };

  const handleImportSuccess = () => {
    fetchCandidates();
    setImportDialogOpen(false);
  };

  const handleDownloadTemplate = () => {
    // Create CSV template
    const headers = ['SN', 'Role', 'Country', 'City', 'Candidate Name', 'Skillset', 'Years Experience', 'LinkedIn URL', 'Past Projects'];
    const csvContent = headers.join(',') + '\n' + 
      '1,Software Engineer,USA,New York,John Doe,React TypeScript Node.js,5,https://linkedin.com/in/johndoe,Project A Project B';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'internal_candidates_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openEditDialog = (candidate: InternalCandidate) => {
    setSelectedCandidate(candidate);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (candidate: InternalCandidate) => {
    setSelectedCandidate(candidate);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">Existing Candidates</h3>
          <p className="text-sm text-primary-light">
            Load and manage existing candidates (both internal candidates and external candidates who have provided their resumes)
          </p>
        </div>
        
        <div className="flex gap-2">
          
          <Button
            onClick={() => setImportDialogOpen(true)}
            className="text-black bg-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Excel
          </Button>

          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-primary hover:bg-primary/90 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Candidate
          </Button>
        </div>
      </div>

      <InternalCandidatesTable
        candidates={candidates}
        loading={loading}
        onEdit={openEditDialog}
        onDelete={openDeleteDialog}
      />

      <AddCandidateDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAdd={handleAddCandidate}
        nextSN={candidates.length + 1}
      />

      <ImportCandidatesDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        projectId={projectId || ''}
        onSuccess={handleImportSuccess}
      />

      {selectedCandidate && (
        <>
          <EditCandidateDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            candidate={selectedCandidate}
            onEdit={handleEditCandidate}
          />

          <DeleteCandidateDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            candidate={selectedCandidate}
            onDelete={handleDeleteCandidate}
          />
        </>
      )}
    </div>
  );
}
